import React from "react";
import { CredentialSecretField } from "./CredentialSecretField";

// Shared inline style tokens + the transport-discriminated destination
// sub-form, extracted from ExportJobMappingModal so support's template
// editor (ExportTemplatesAdmin — DEV-2116) can reuse the same field layout
// for base_destination_config without duplicating it. `showCredential`
// defaults to true (ExportJobMappingModal's job-level behavior, unchanged);
// pass false to render only the endpoint/host/path/header-shape fields — no
// Vault credential field, and no credential-injection chooser, since both
// are meaningless without a per-advertiser credential attached.

export const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  background: "#FAFAFA",
  border: "1px solid #E2E2E2",
  borderRadius: 8,
  color: "#151515",
  fontSize: 13,
  marginBottom: 10,
  outline: "none",
};

export const labelStyle = { fontSize: 11, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 };

// The wrapping <label> gives the input its accessible name. errorId, when
// set, marks the input invalid and points it at the element holding the error.
export function TextField({ label, value, onChange, placeholder, type = "text", errorId }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ ...labelStyle, display: "block" }}>{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
      />
    </label>
  );
}

// HTTP POST-only sub-form for `headers` — a plain object of custom header
// name/value pairs sent with every request (see 03-design.md's documented
// destination_config shape: {"headers":{"X-Source":"audiohook"}, ...}).
// Rows are derived straight from the `headers` object on every render (no
// separate local row state) since this is a small, rarely-edited set.
export function HeadersEditor({ headers, onChange }) {
  const rows = Object.entries(headers || {});
  const updateRow = (idx, newKey, newValue) => {
    const next = rows.map((row, i) => (i === idx ? [newKey, newValue] : row));
    onChange(Object.fromEntries(next));
  };
  const removeRow = (idx) => onChange(Object.fromEntries(rows.filter((_, i) => i !== idx)));
  const addRow = () => onChange({ ...(headers || {}), "": "" });

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={labelStyle}>Custom Headers</div>
      {rows.map(([key, value], idx) => (
        <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input
            value={key}
            onChange={(e) => updateRow(idx, e.target.value, value)}
            placeholder="Header-Name"
            aria-label="Header name"
            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
          />
          <input
            value={value}
            onChange={(e) => updateRow(idx, key, e.target.value)}
            placeholder="value"
            aria-label="Header value"
            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
          />
          <button type="button" onClick={() => removeRow(idx)} aria-label="Remove header" style={{ background: "none", border: "none", color: "#7b7b7b", cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
      ))}
      <button type="button" onClick={addRow} style={{ padding: "6px 12px", background: "transparent", border: "1px dashed #E2E2E2", borderRadius: 8, color: "#626262", fontSize: 12, cursor: "pointer" }}>+ Add header</button>
    </div>
  );
}

// Renders the transport-specific fields for one environment block
// (production or test) — same field set for both, per transport.
// `credentialDescription` is a short human label (e.g. "sftp / production")
// stored alongside the Vault secret for support's own reference; the raw
// value itself is written write-only via CredentialSecretField.
// `showRakutenMid` adds the advertiser's Rakuten MID (stored as `mid`): the
// cycle-scheduled-tasks worker puts it in the Rakuten O2O file name
// (`<mid>_o2o-trans_<YYYYMMDD>*.json`) and refuses to upload without it.
export function TransportFields({ transportType, values, onChange, credentialDescription, advertiserId, showCredential = true, showRakutenMid = false, midErrorId }) {
  const set = (key) => (val) => onChange({ ...values, [key]: val });
  const credentialField = showCredential ? (
    <CredentialSecretField
      credentialSecretId={values.credential_secret_id}
      onCredentialCreated={set("credential_secret_id")}
      description={credentialDescription}
      advertiserId={advertiserId}
    />
  ) : null;
  if (transportType === "sftp") {
    return (
      <>
        <TextField label="Host" value={values.host} onChange={set("host")} placeholder="sftp.partner.com" />
        <TextField label="Port" value={values.port} onChange={set("port")} placeholder="22" type="number" />
        <TextField label="Username" value={values.username} onChange={set("username")} placeholder="username" />
        <TextField label="Remote Path" value={values.remote_path} onChange={set("remote_path")} placeholder="/incoming/exports" />
        {showRakutenMid && (
          <TextField label="Rakuten MID" value={values.mid} onChange={set("mid")} placeholder="e.g. 12345" errorId={midErrorId} />
        )}
        {credentialField}
      </>
    );
  }
  if (transportType === "http_get") {
    return (
      <>
        <TextField label="URL Template" value={values.url_template} onChange={set("url_template")} placeholder="https://api.partner.com/track?oid={orderId}" />
        {showCredential && (
          <TextField label="Credential Query Param Name" value={values.credential_injection?.name} onChange={(v) => onChange({ ...values, credential_injection: { type: "query_param", name: v } })} placeholder="api_key" />
        )}
        {credentialField}
      </>
    );
  }
  if (transportType === "http_post") {
    return (
      <>
        <TextField label="Endpoint URL" value={values.endpoint_url} onChange={set("endpoint_url")} placeholder="https://api.partner.com/webhook" />
        <div style={{ marginBottom: 10 }}>
          <div style={labelStyle}>Body Format</div>
          <select value={values.body_format || "json"} onChange={(e) => set("body_format")(e.target.value)} style={{ ...inputStyle, paddingRight: 32 }}>
            <option value="json">JSON</option>
            <option value="form">Form-encoded</option>
          </select>
        </div>
        <HeadersEditor headers={values.headers} onChange={set("headers")} />
        {showCredential && (
          <div>
            <div style={labelStyle}>Credential Injection</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {["bearer", "header"].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onChange({ ...values, credential_injection: { type: opt, ...(opt === "header" ? { name: values.credential_injection?.name || "X-Api-Key" } : {}) } })}
                  style={{
                    padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    border: `1px solid ${values.credential_injection?.type === opt ? "#F90091" : "#E2E2E2"}`,
                    background: values.credential_injection?.type === opt ? "#FFE9F6" : "#FFFFFF",
                    color: values.credential_injection?.type === opt ? "#F90091" : "#626262",
                  }}
                >
                  {opt === "bearer" ? "Bearer token" : "Custom header"}
                </button>
              ))}
            </div>
          </div>
        )}
        {showCredential && values.credential_injection?.type === "header" && (
          <TextField label="Header Name" value={values.credential_injection?.name} onChange={(v) => onChange({ ...values, credential_injection: { type: "header", name: v } })} placeholder="X-Api-Key" />
        )}
        {credentialField}
      </>
    );
  }
  return null;
}
